(() => {
  const root = document.querySelector('[data-aonsk-grower]');
  if (!root) return;

  const form = root.querySelector('.aonsk-grower__form');
  if (!form) return;

  root.classList.add('is-enhanced');
  document.documentElement.classList.add('aonsk-grower-js');

  const steps = Array.from(form.querySelectorAll('[data-grower-step]'));
  const progressItems = Array.from(form.querySelectorAll('[data-grower-progress]'));
  const pathInputs = Array.from(form.querySelectorAll('[data-grower-path]'));
  const pathLinks = Array.from(root.querySelectorAll('[data-grower-path-link]'));
  const review = form.querySelector('[data-grower-review]');
  const saveToggle = form.querySelector('[data-grower-save-progress]');
  const draftBanner = form.querySelector('[data-grower-draft-banner]');
  const restoreButton = form.querySelector('[data-grower-draft-restore]');
  const discardButton = form.querySelector('[data-grower-draft-discard]');
  const successMessage = form.querySelector('[data-grower-success]');
  const submitButton = form.querySelector('button[type="submit"]');
  const draftKey = 'aonskSeedGrowerDraftV1043';
  let currentStep = 1;
  let saveTimer = null;

  const safelyParse = (value) => {
    try { return JSON.parse(value); } catch (_error) { return null; }
  };

  const escapeSelector = (value) => {
    if (window.CSS?.escape) return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  };

  const getStoredDraft = () => {
    try { return safelyParse(window.localStorage.getItem(draftKey)); } catch (_error) { return null; }
  };

  const removeStoredDraft = () => {
    try { window.localStorage.removeItem(draftKey); } catch (_error) { /* Storage can be unavailable. */ }
  };

  if (successMessage) {
    removeStoredDraft();
    return;
  }
  if (!steps.length) return;
  form.noValidate = true;

  const selectedPath = () => pathInputs.find((input) => input.checked)?.dataset.growerPath || '';

  const pathAllows = (step) => {
    const requirement = step.dataset.pathStep;
    if (!requirement) return true;
    const path = selectedPath();
    return path === 'both' || path === requirement;
  };

  const setFieldAvailability = (scope, active) => {
    scope.querySelectorAll('input, select, textarea, button').forEach((field) => {
      if (field.matches('[data-grower-next], [data-grower-back], button[type="submit"]')) return;
      if (!active) {
        if (field.required || field.dataset.pathRequired === 'true') field.dataset.wasRequired = 'true';
        field.required = false;
        field.disabled = true;
      } else {
        field.disabled = false;
        if (field.dataset.wasRequired === 'true' || field.dataset.pathRequired === 'true') field.required = true;
      }
    });
  };

  const activeSteps = () => steps.filter((step) => !step.classList.contains('is-path-hidden'));

  const updatePathSteps = () => {
    steps.forEach((step) => {
      const visible = pathAllows(step);
      step.classList.toggle('is-path-hidden', !visible);
      if (step.dataset.pathStep) setFieldAvailability(step, visible);
    });

    progressItems.forEach((item) => {
      const requirement = item.dataset.pathProgress;
      const path = selectedPath();
      const visible = !requirement || path === 'both' || path === requirement;
      item.classList.toggle('is-path-hidden', !visible);
    });

    progressItems
      .filter((item) => !item.classList.contains('is-path-hidden'))
      .forEach((item, index) => {
        const number = item.querySelector('button > span');
        if (number) number.textContent = String(index + 1);
      });

    const current = steps.find((step) => Number(step.dataset.growerStep) === currentStep);
    if (current && current.classList.contains('is-path-hidden')) {
      const fallback = activeSteps().find((step) => Number(step.dataset.growerStep) > currentStep) || activeSteps().at(-1);
      currentStep = Number(fallback?.dataset.growerStep || 1);
    }
  };

  const conditionControllers = Array.from(form.querySelectorAll('[data-condition-controller]'));
  const conditionalGroups = Array.from(form.querySelectorAll('[data-condition-field]'));

  const controllerValues = (key) => {
    const values = [];
    conditionControllers
      .filter((control) => control.dataset.conditionController === key)
      .forEach((control) => {
        if ((control.type === 'radio' || control.type === 'checkbox') && control.checked) values.push(control.value);
        else if (control.tagName === 'SELECT' && control.value) values.push(control.value);
        else if (!['radio', 'checkbox'].includes(control.type) && control.tagName !== 'SELECT' && control.value) values.push(control.value);
      });
    return values;
  };

  const updateConditionals = () => {
    conditionalGroups.forEach((group) => {
      const key = group.dataset.conditionField || '';
      const accepted = (group.dataset.conditionValues || '')
        .split('|')
        .map((value) => value.trim())
        .filter(Boolean);
      const active = controllerValues(key).some((value) => accepted.includes(value));
      group.classList.toggle('is-active', active);
      group.setAttribute('aria-hidden', active ? 'false' : 'true');
      setFieldAvailability(group, active);
    });
  };

  conditionControllers.forEach((control) => control.addEventListener('change', updateConditionals));

  const firstInvalid = (scope) => scope.querySelector('input:invalid:not([disabled]), select:invalid:not([disabled]), textarea:invalid:not([disabled])');

  const focusProblem = (element) => {
    if (!element) return;
    element.focus?.({ preventScroll: true });
    element.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  };

  const validateCheckboxGroups = (step, shouldFocus = true) => {
    let valid = true;
    step.querySelectorAll('[data-required-checkbox-group]').forEach((group) => {
      const name = group.dataset.requiredCheckboxGroup || '';
      const checked = Array.from(group.querySelectorAll('input[type="checkbox"]:not([disabled])')).some((field) => field.checked);
      const error = group.querySelector(`[data-group-error="${escapeSelector(name)}"]`) || group.querySelector('[data-group-error]');
      if (error) error.hidden = checked;
      group.classList.toggle('has-error', !checked);
      if (!checked && valid) {
        valid = false;
        if (shouldFocus) focusProblem(group.querySelector('input[type="checkbox"]'));
      }
    });
    return valid;
  };

  const validateRadioGroups = (step, shouldFocus = true) => {
    let valid = true;
    step.querySelectorAll('[data-required-radio-group]').forEach((group) => {
      const name = group.dataset.requiredRadioGroup || '';
      const checked = Array.from(group.querySelectorAll('input[type="radio"]:not([disabled])')).some((field) => field.checked);
      const error = group.querySelector(`[data-group-error="${escapeSelector(name)}"]`) || group.querySelector('[data-group-error]');
      if (error) error.hidden = checked;
      group.classList.toggle('has-error', !checked);
      if (!checked && valid) {
        valid = false;
        if (shouldFocus) focusProblem(group.querySelector('input[type="radio"]'));
      }
    });
    return valid;
  };

  form.querySelectorAll('[data-required-checkbox-group] input[type="checkbox"], [data-required-radio-group] input[type="radio"]').forEach((field) => {
    field.addEventListener('change', () => {
      const group = field.closest('[data-required-checkbox-group], [data-required-radio-group]');
      if (!group) return;
      const type = field.type;
      const valid = Array.from(group.querySelectorAll(`input[type="${type}"]:not([disabled])`)).some((item) => item.checked);
      const error = group.querySelector('[data-group-error]');
      if (error) error.hidden = valid;
      group.classList.toggle('has-error', !valid);
    });
  });

  const validateStep = (stepNumber, shouldFocus = true) => {
    const step = steps.find((item) => Number(item.dataset.growerStep) === Number(stepNumber));
    if (!step || step.classList.contains('is-path-hidden')) return true;
    updateConditionals();
    const checkboxValid = validateCheckboxGroups(step, shouldFocus);
    const radioValid = validateRadioGroups(step, shouldFocus);
    const invalid = firstInvalid(step);
    if (invalid) {
      if (shouldFocus) {
        invalid.reportValidity?.();
        focusProblem(invalid);
      }
      return false;
    }
    return checkboxValid && radioValid;
  };

  const labelFromName = (name) => name
    .replace(/^contact\[/, '')
    .replace(/\]$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

  const controlValue = (control) => {
    if (control.type === 'checkbox' || control.type === 'radio') return control.checked ? control.value : '';
    if (control.tagName === 'SELECT') return control.value ? control.options[control.selectedIndex]?.text || '' : '';
    return String(control.value || '').trim();
  };

  const buildReview = () => {
    if (!review) return;
    review.textContent = '';
    activeSteps()
      .filter((step) => Number(step.dataset.growerStep) < 7)
      .forEach((step) => {
        const card = document.createElement('section');
        card.className = 'aonsk-grower__review-card';
        const heading = document.createElement('h3');
        heading.textContent = step.dataset.stepTitle || '';
        card.appendChild(heading);
        const list = document.createElement('dl');
        const seen = new Set();

        const addEntry = (label, value) => {
          const cleanLabel = String(label || '').trim();
          const cleanValue = String(value || '').trim();
          if (!cleanLabel || !cleanValue || seen.has(`${cleanLabel}|${cleanValue}`)) return;
          seen.add(`${cleanLabel}|${cleanValue}`);
          const row = document.createElement('div');
          const term = document.createElement('dt');
          const detail = document.createElement('dd');
          term.textContent = cleanLabel;
          detail.textContent = cleanValue;
          row.append(term, detail);
          list.appendChild(row);
        };

        step.querySelectorAll('[data-summary-label]:not([disabled])').forEach((control) => {
          addEntry(control.dataset.summaryLabel, controlValue(control));
        });

        const radioNames = new Set(Array.from(step.querySelectorAll('input[type="radio"]:checked:not([disabled])')).map((control) => control.name));
        radioNames.forEach((name) => {
          const control = step.querySelector(`input[type="radio"][name="${escapeSelector(name)}"]:checked:not([disabled])`);
          if (control) addEntry(labelFromName(name), control.value);
        });

        step.querySelectorAll('.aonsk-grower__checkbox-fieldset').forEach((fieldset) => {
          const checked = Array.from(fieldset.querySelectorAll('input[type="checkbox"]:checked:not([disabled])'))
            .map((control) => control.closest('label')?.textContent?.trim())
            .filter(Boolean);
          if (checked.length) addEntry(fieldset.querySelector('legend')?.textContent?.replace('*', '').trim() || 'Selected items', checked.join(', '));
        });

        if (!list.children.length) addEntry(root.dataset.reviewStatus, root.dataset.emptyReview);
        card.appendChild(list);
        review.appendChild(card);
      });
  };

  const serializeDraft = () => {
    const values = {};
    Array.from(form.elements).forEach((control) => {
      if (!control.name?.startsWith('contact[')) return;
      if (['contact[Application source]', 'contact[Application submitted]', 'contact[Application type]'].includes(control.name)) return;
      if (control.type === 'radio' || control.type === 'checkbox') {
        if (!control.checked) return;
        if (!Array.isArray(values[control.name])) values[control.name] = [];
        values[control.name].push(control.value);
      } else {
        values[control.name] = control.value;
      }
    });
    return { version: 1043, savedAt: Date.now(), currentStep, values };
  };

  const saveDraft = () => {
    if (!saveToggle?.checked) return;
    try { window.localStorage.setItem(draftKey, JSON.stringify(serializeDraft())); } catch (_error) { /* Storage can be unavailable. */ }
  };

  const scheduleSave = () => {
    if (!saveToggle?.checked) return;
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(saveDraft, 250);
  };

  const restoreDraft = (draft) => {
    if (!draft?.values) return;
    Object.entries(draft.values).forEach(([name, value]) => {
      const controls = Array.from(form.querySelectorAll(`[name="${escapeSelector(name)}"]`));
      if (!controls.length) return;
      if (Array.isArray(value)) controls.forEach((control) => { control.checked = value.includes(control.value); });
      else controls.forEach((control) => { control.value = value; });
    });
    if (saveToggle) saveToggle.checked = true;
    updatePathSteps();
    updateConditionals();
    setStep(Number(draft.currentStep) || 1, false);
    saveDraft();
  };

  const storedDraft = getStoredDraft();
  if (storedDraft && draftBanner && !successMessage) draftBanner.hidden = false;

  restoreButton?.addEventListener('click', () => {
    restoreDraft(getStoredDraft());
    if (draftBanner) draftBanner.hidden = true;
  });

  discardButton?.addEventListener('click', () => {
    removeStoredDraft();
    if (draftBanner) draftBanner.hidden = true;
    if (saveToggle) saveToggle.checked = false;
  });

  saveToggle?.addEventListener('change', () => {
    if (saveToggle.checked) saveDraft();
    else removeStoredDraft();
  });

  form.addEventListener('input', scheduleSave);
  form.addEventListener('change', scheduleSave);

  const setStep = (stepNumber, shouldScroll = true) => {
    updatePathSteps();
    const available = activeSteps();
    const requested = steps.find((step) => Number(step.dataset.growerStep) === Number(stepNumber) && !step.classList.contains('is-path-hidden'));
    const target = requested || available.find((step) => Number(step.dataset.growerStep) >= Number(stepNumber)) || available.at(-1) || steps[0];
    currentStep = Number(target.dataset.growerStep || 1);

    steps.forEach((step) => step.classList.toggle('is-active', step === target));
    const currentIndex = available.indexOf(target);
    progressItems.forEach((item) => {
      if (item.classList.contains('is-path-hidden')) return;
      const matchingStep = steps.find((step) => Number(step.dataset.growerStep) === Number(item.dataset.growerProgress));
      const index = available.indexOf(matchingStep);
      item.classList.toggle('is-current', index === currentIndex);
      item.classList.toggle('is-complete', index >= 0 && index < currentIndex);
      const button = item.querySelector('button');
      if (button) button.setAttribute('aria-current', index === currentIndex ? 'step' : 'false');
    });

    if (currentStep === 7) buildReview();
    updateConditionals();
    saveDraft();

    if (shouldScroll) {
      form.querySelector('.aonsk-grower__progress')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => target.querySelector('input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])')?.focus({ preventScroll: true }), 320);
    }
  };

  const moveBy = (direction) => {
    const available = activeSteps();
    const current = steps.find((step) => Number(step.dataset.growerStep) === currentStep);
    const index = available.indexOf(current);
    const target = available[index + direction];
    if (!target) return;
    if (direction > 0 && !validateStep(currentStep)) return;
    setStep(Number(target.dataset.growerStep));
  };

  form.querySelectorAll('[data-grower-next]').forEach((button) => button.addEventListener('click', () => moveBy(1)));
  form.querySelectorAll('[data-grower-back]').forEach((button) => button.addEventListener('click', () => moveBy(-1)));

  form.querySelectorAll('[data-grower-step-target]').forEach((button) => {
    button.addEventListener('click', () => {
      const targetNumber = Number(button.dataset.growerStepTarget);
      const available = activeSteps();
      const current = steps.find((step) => Number(step.dataset.growerStep) === currentStep);
      const target = steps.find((step) => Number(step.dataset.growerStep) === targetNumber && !step.classList.contains('is-path-hidden'));
      if (!target) return;
      const currentIndex = available.indexOf(current);
      const targetIndex = available.indexOf(target);
      if (targetIndex > currentIndex) {
        for (let i = currentIndex; i < targetIndex; i += 1) {
          const number = Number(available[i].dataset.growerStep);
          if (!validateStep(number, false)) {
            setStep(number, false);
            validateStep(number, true);
            return;
          }
        }
      }
      setStep(targetNumber);
    });
  });

  pathInputs.forEach((input) => {
    input.addEventListener('change', () => {
      updatePathSteps();
      updateConditionals();
      scheduleSave();
    });
  });

  pathLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      const path = link.dataset.growerPathLink;
      const input = pathInputs.find((item) => item.dataset.growerPath === path);
      if (!input) return;
      event.preventDefault();
      input.checked = true;
      updatePathSteps();
      setStep(1, false);
      root.querySelector('#grower-application')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => input.focus({ preventScroll: true }), 450);
    });
  });

  form.addEventListener('submit', (event) => {
    updatePathSteps();
    updateConditionals();
    const available = activeSteps();
    for (const step of available) {
      const number = Number(step.dataset.growerStep);
      if (!validateStep(number, false)) {
        event.preventDefault();
        setStep(number, false);
        window.requestAnimationFrame(() => {
          const invalid = firstInvalid(step);
          const invalidGroup = step.querySelector('.has-error');
          if (invalid) {
            invalid.reportValidity?.();
            focusProblem(invalid);
          } else if (invalidGroup) {
            focusProblem(invalidGroup.querySelector('input'));
          }
        });
        return;
      }
    }
    if (!form.checkValidity()) return;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = submitButton.dataset.submittingText;
      window.setTimeout(() => {
        submitButton.disabled = false;
        submitButton.textContent = submitButton.dataset.submitText;
      }, 14000);
    }
  });

  const requestedPath = new URLSearchParams(window.location.search).get('path');
  if (['trial', 'contract', 'both'].includes(requestedPath)) {
    const input = pathInputs.find((item) => item.dataset.growerPath === requestedPath);
    if (input) input.checked = true;
  }

  updatePathSteps();
  updateConditionals();
  setStep(1, false);
})();
